import LegalLayout from "@/components/legal/LegalLayout";

const TOC = [
  { id: "who", label: "Who we are" },
  { id: "collect", label: "What we collect" },
  { id: "use", label: "How we use it, and why" },
  { id: "emails", label: "Marketing emails" },
  { id: "cookies", label: "Cookies" },
  { id: "share", label: "Who we share it with" },
  { id: "transfers", label: "International transfers" },
  { id: "retention", label: "How long we keep it" },
  { id: "security", label: "Security and data breaches" },
  { id: "rights", label: "Your rights" },
  { id: "children", label: "Children" },
  { id: "changes", label: "Changes to this policy" },
  { id: "contact", label: "Contact and Grievance Officer" },
];

const SUMMARY = [
  "We collect only what we need: your name, email and order details.",
  "We never see or store your card details.",
  "We never sell your data or use it for targeted advertising.",
  "Marketing emails only with your consent, and you can unsubscribe any time.",
  "You can access, correct or delete your data by emailing us.",
];

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" activePath="/privacy" summary={SUMMARY} toc={TOC}>
      <section className="tc__sec" id="who">
        <h2>Who we are</h2>
        <p>This Privacy Policy explains how Rajesh Nuthalapati, publishing as Rajesh Sivacharya ("we", "us"), collects and uses your personal data when you visit rajeshsivacharya.com, sign up for our emails, or buy our products.</p>
        <p>We are the "Data Fiduciary" under India's Digital Personal Data Protection Act, 2023 (DPDP Act) and the "controller" under the EU and UK General Data Protection Regulation (GDPR).</p>
        <div className="tc__card">
          <p><strong>Seller:</strong> Rajesh Nuthalapati, an individual seller, publishing as Rajesh Sivacharya</p>
          <p><strong>Address:</strong> Flat No. 409, Thatipalli Lake View Apartments, 4th Floor, Priyadarshini Colony, Paloncha Town, Bhadradri Kothagudem District, Telangana – 507115</p>
          <p><strong>Email:</strong> <a href="mailto:support@rajeshsivacharya.com">support@rajeshsivacharya.com</a></p>
        </div>
      </section>

      <section className="tc__sec" id="collect">
        <h2>What we collect</h2>
        <h3>Information you give us</h3>
        <ul>
          <li><strong>Name and email address</strong>, when you buy a product, sign up for the free guide or our emails, or contact us.</li>
          <li><strong>Your messages</strong>, when you email us for support, refunds or questions.</li>
        </ul>
        <h3>Information from our Payment Provider</h3>
        <ul>
          <li><strong>Order details</strong>: the product bought, price, currency, country, date and order number.</li>
          <li>We <strong>never</strong> receive or store your full card number, CVV or bank login. These are handled only by the Payment Provider.</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li><strong>Technical data</strong>: IP address, browser and device type, and pages requested, recorded in standard server logs by our hosting provider for security and reliability.</li>
          <li><strong>Cookies and similar technologies</strong>, as described in the Cookies section below.</li>
        </ul>
        <p>We do not knowingly collect sensitive data such as health, financial account or government ID information, and we ask you not to send it to us. Our health and wealth journals are completed privately by you; nothing you write in them is ever sent to us.</p>
      </section>

      <section className="tc__sec" id="use">
        <h2>How we use it, and why</h2>
        <p>We use your data only for the purposes below. Under the DPDP Act we rely on your consent or on a legitimate use permitted by the Act; under the GDPR, the legal basis is shown for each purpose.</p>
        <div className="tc__table-wrap">
          <table className="tc__table">
            <thead><tr><th scope="col">Purpose</th><th scope="col">Data used</th><th scope="col">Legal basis (GDPR)</th></tr></thead>
            <tbody>
              <tr><td>Delivering your purchase and download links</td><td>Name, email, order details</td><td>Performance of a contract</td></tr>
              <tr><td>Customer support, refunds and complaints</td><td>Name, email, messages, order details</td><td>Performance of a contract</td></tr>
              <tr><td>Keeping tax, accounting and transaction records</td><td>Order details</td><td>Legal obligation</td></tr>
              <tr><td>Sending the free guide and marketing emails</td><td>Name, email</td><td>Consent (you can withdraw any time)</td></tr>
              <tr><td>Keeping the Website secure and preventing fraud</td><td>Technical data</td><td>Legitimate interests</td></tr>
              <tr><td>Understanding which pages are useful (only if analytics is enabled)</td><td>Technical data, cookies</td><td>Consent</td></tr>
            </tbody>
          </table>
        </div>
        <p>We do not use your data for automated decision-making that has legal or similarly significant effects on you, and we do not build advertising profiles about you.</p>
      </section>

      <section className="tc__sec" id="emails">
        <h2>Marketing emails</h2>
        <p>We send marketing emails only if you have opted in, for example by requesting our free guide. Each email includes an unsubscribe link, and you can also email us to stop. Unsubscribing does not affect emails about a purchase you have made, such as download links or refund confirmations.</p>
      </section>

      <section className="tc__sec" id="cookies">
        <h2>Cookies</h2>
        <p>We use only <strong>essential cookies</strong> needed for the Website and checkout to work, such as keeping your session secure. These do not require consent.</p>
        <p>If we add analytics or advertising cookies in future, we will ask for your consent first, where the law requires it, through a cookie banner, and we will list those cookies here.</p>
        <p>Our pages load fonts from Google Fonts, which means your browser connects to Google's servers and shares your IP address with Google.</p>
      </section>

      <section className="tc__sec" id="share">
        <h2>Who we share it with</h2>
        <p>We never sell your personal data, and we do not share it for cross-context behavioural advertising. We share it only with service providers who help us run the store, under contracts that require them to protect it:</p>
        <div className="tc__table-wrap">
          <table className="tc__table">
            <thead><tr><th scope="col">Provider</th><th scope="col">Purpose</th><th scope="col">Location</th></tr></thead>
            <tbody>
              <tr><td>Dodo Payments</td><td>Payment processing, tax collection, refunds (as Merchant of Record and an independent controller of payment data)</td><td>India</td></tr>
              <tr><td>Render</td><td>Website hosting and server logs</td><td>United States</td></tr>
              <tr><td>MilesWeb</td><td>Delivering emails and download links</td><td>India</td></tr>
              <tr><td>MilesWeb</td><td>Customer support email</td><td>India</td></tr>
              <tr><td>Google Fonts</td><td>Loading website fonts</td><td>United States</td></tr>
            </tbody>
          </table>
        </div>
        <p>We may also disclose data where required by law, by a court or government authority, or to protect our rights, our customers or the public.</p>
      </section>

      <section className="tc__sec" id="transfers">
        <h2>International transfers</h2>
        <p>Some of our providers store data outside your country, including in the United States. Where data leaves the EU, UK or India, we rely on safeguards recognised by law, such as the European Commission's Standard Contractual Clauses, and on our providers' security commitments. Transfers out of India follow any restrictions notified under the DPDP Act.</p>
      </section>

      <section className="tc__sec" id="retention">
        <h2>How long we keep it</h2>
        <div className="tc__table-wrap">
          <table className="tc__table">
            <thead><tr><th scope="col">Data</th><th scope="col">Kept for</th></tr></thead>
            <tbody>
              <tr><td>Order and transaction records</td><td>As long as Indian tax and accounting laws require (generally up to 8 years)</td></tr>
              <tr><td>Support and refund emails</td><td>Up to 2 years after the matter is closed</td></tr>
              <tr><td>Email list (name, email)</td><td>Until you unsubscribe, then deleted or suppressed so we do not email you again</td></tr>
              <tr><td>Server logs</td><td>Up to 90 days</td></tr>
            </tbody>
          </table>
        </div>
        <p>When data is no longer needed, we delete or anonymise it.</p>
      </section>

      <section className="tc__sec" id="security">
        <h2>Security and data breaches</h2>
        <p>We use reasonable security safeguards, including encrypted connections (HTTPS), access controls on our accounts and providers that meet recognised security standards. No online system is perfectly secure, but we work to protect your data.</p>
        <p>If a personal data breach occurs, we will notify the Data Protection Board of India and affected individuals as required by the DPDP Act and its Rules, and the relevant supervisory authority within 72 hours where the GDPR applies.</p>
      </section>

      <section className="tc__sec" id="rights">
        <h2>Your rights</h2>
        <p>You can exercise any of these rights free of charge by emailing <a href="mailto:support@rajeshsivacharya.com">support@rajeshsivacharya.com</a>. We may ask you to confirm your identity first. We respond within 30 days.</p>
        <h3>In India (DPDP Act, 2023)</h3>
        <ul>
          <li>Access a summary of the personal data we hold about you and how we process it.</li>
          <li>Correction, completion, updating and erasure of your data.</li>
          <li>Withdraw consent at any time, as easily as you gave it. This does not affect processing done before withdrawal.</li>
          <li>Grievance redressal through our Grievance Officer, and, if unresolved, a complaint to the Data Protection Board of India.</li>
          <li>Nominate another person to exercise your rights in the event of your death or incapacity.</li>
        </ul>
        <h3>In the EU and UK (GDPR)</h3>
        <ul>
          <li>Access, correction, erasure, restriction and portability of your data.</li>
          <li>Object to processing based on legitimate interests, and to direct marketing at any time.</li>
          <li>Withdraw consent at any time.</li>
          <li>Complain to your local data protection authority, or in the UK to the Information Commissioner's Office.</li>
        </ul>
        <h3>In California and other US states</h3>
        <ul>
          <li>Know what personal information we collect, use and disclose, and request its deletion or correction.</li>
          <li>Opt out of the sale or sharing of personal information. We do not sell or share it.</li>
          <li>Not be discriminated against for exercising these rights.</li>
        </ul>
      </section>

      <section className="tc__sec" id="children">
        <h2>Children</h2>
        <p>Our products and Website are intended for adults. We do not knowingly collect personal data from anyone under 18. Under the DPDP Act, processing a child's data requires verifiable parental consent, so if you believe a child has given us personal data, please contact us and we will delete it.</p>
      </section>

      <section className="tc__sec" id="changes">
        <h2>Changes to this policy</h2>
        <p>We may update this Privacy Policy. The new version will be posted here with a new "Last updated" date, and we will notify you by email of any significant change that affects how we use your data.</p>
      </section>

      <section className="tc__sec" id="contact">
        <h2>Contact and Grievance Officer</h2>
        <p>For any privacy question, request or complaint, contact our Grievance Officer:</p>
        <div className="tc__card">
          <p><strong>Grievance Officer:</strong> Rajesh Nuthalapati</p>
          <p><strong>Email:</strong> <a href="mailto:support@rajeshsivacharya.com">support@rajeshsivacharya.com</a></p>
          <p><strong>Address:</strong> Flat No. 409, Thatipalli Lake View Apartments, 4th Floor, Priyadarshini Colony, Paloncha Town, Bhadradri Kothagudem District, Telangana – 507115</p>
          <p><strong>Hours:</strong> Monday to Friday, 10:00 to 18:00 IST</p>
        </div>
        <p>If you are in India and are not satisfied with our response, you may approach the Data Protection Board of India.</p>
      </section>
    </LegalLayout>
  );
}
